import { useState } from 'react';
import { Layers, Send } from 'lucide-react';
import { formatCount } from '@/utils/number';
import { PCMP_REDISBURSE_ACTION_COPY } from '../../constants/pcmpAccounts.constants';
import { useCreatePcmpRedisburseBatch } from '../../hooks/useCreatePcmpRedisburseBatch';
import { useDisbursePcmpRedisburseBatch } from '../../hooks/useDisbursePcmpRedisburseBatch';
import type {
  PcmpAccountsBatchSelection,
  PcmpRedisburseCreateSelection,
} from '../../types/pcmpAccounts.types';
import { PcmpBulkCommentDialog, type PcmpBulkOutcome } from '../PcmpBatch/PcmpBulkCommentDialog';

interface TargetBase {
  count: number;
  amount: number;
  /** What the selection covers, e.g. "Selected proposals". */
  scope: string;
}

/** Corrected proposals from the Reverified tab into a new batch, or a batch's members paid. */
export type PcmpRedisburseBulkTarget =
  | (TargetBase & { action: 'CREATE'; selection: PcmpRedisburseCreateSelection })
  | (TargetBase & { action: 'DISBURSE'; batchNo: number; selection: PcmpAccountsBatchSelection });

interface PcmpRedisburseBulkDialogProps {
  target: PcmpRedisburseBulkTarget;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  /** Called once the API has acted, so the caller can clear its selection. */
  onDone: () => void;
  /** After a new batch is created (and any left-out proposals were shown). */
  onCreated?: ((batchNo: number) => void) | undefined;
  onClose: () => void;
}

const LEFT_OUT = 'The ones below changed since the list was loaded and were left out.';

/**
 * Legacy CreateRedisburseBatch and RedisbursePendingDisburseBatch / Selected.
 * Both hooks are created, only the target's runs.
 */
export function PcmpRedisburseBulkDialog({
  target,
  refNoOf,
  onDone,
  onCreated,
  onClose,
}: PcmpRedisburseBulkDialogProps) {
  const isCreate = target.action === 'CREATE';
  const copy = PCMP_REDISBURSE_ACTION_COPY[target.action];
  const [outcome, setOutcome] = useState<PcmpBulkOutcome | null>(null);
  const [createdNo, setCreatedNo] = useState<number | null>(null);

  const create = useCreatePcmpRedisburseBatch((result) => {
    onDone();
    if (result.skipped.length === 0) {
      onCreated?.(result.batchNo);
      onClose();
      return;
    }
    setCreatedNo(result.batchNo);
    setOutcome({
      title: `Re-disbursement batch #${result.batchNo} created`,
      summary: `${formatCount(result.added.length, 'proposal')} batched. ${LEFT_OUT}`,
      skipped: result.skipped,
    });
  });
  const disburse = useDisbursePcmpRedisburseBatch(
    target.action === 'DISBURSE' ? target.batchNo : 0,
    (result) => {
      onDone();
      if (result.skipped.length === 0) {
        onClose();
        return;
      }
      setOutcome({
        title: 'Re-disbursed',
        summary: `${formatCount(result.disbursed.length, 'proposal')} re-disbursed. ${LEFT_OUT}`,
        skipped: result.skipped,
      });
    },
  );
  const { isPending, error } = isCreate ? create : disburse;

  const close = () => {
    if (createdNo !== null) onCreated?.(createdNo);
    onClose();
  };

  return (
    <PcmpBulkCommentDialog
      title={
        target.action === 'DISBURSE' ? `Re-disburse from batch #${target.batchNo}` : copy.title
      }
      description={
        isCreate ? 'A new batch numbered from 9000001' : 'Paid to the corrected accounts'
      }
      icon={isCreate ? Layers : Send}
      submitLabel={copy.submit}
      pendingLabel={copy.pending}
      scope={target.scope}
      count={target.count}
      amount={target.amount}
      note={
        isCreate
          ? 'Each proposal is to be paid again its proposed amount, to its corrected account. Proposals already batched, held or no longer corrected are left out and listed.'
          : 'Each proposal is recorded as paid its proposed amount. Proposals that changed meanwhile (held, failed, already paid) are left out and listed.'
      }
      field={{
        label: 'Comment',
        placeholder: isCreate ? 'Note for the batch' : 'Payment reference or note',
      }}
      isPending={isPending}
      error={error}
      onSubmit={(comment) =>
        target.action === 'CREATE'
          ? create.mutate({ ...target.selection, comment })
          : disburse.mutate({ ...target.selection, comment })
      }
      outcome={outcome}
      refNoOf={refNoOf}
      onClose={close}
    />
  );
}
